/**
 * Cross-tab exclusive lock for customer refresh rotation (G15+G22 / mission G6).
 * Prevents dual-tab rotate → TOKEN_VERSION_MISMATCH → broadcast logout cascades.
 *
 * Live module name: `crossTabRefreshLock.ts` — **BAN** invent
 * `customerCrossTabRefreshLock`.
 *
 * Prefer Web Locks API; fall back to localStorage lease when locks are unavailable.
 * Lock wait never degrades to an unlocked critical section — timeout soft-fails.
 * **BAN** short reuse leeway: after wait budget, throw `RefreshLockTimeoutError`
 * (coordinator maps → soft `REFRESH_LOCK_TIMEOUT`) — never run rotate unlocked.
 */

const LOCK_NAME = 'rpapp-customer-auth-refresh';
const STORAGE_KEY = 'rpapp-crosstab:customer-auth-refresh-lock';

/** Storage-lock spin budget before soft-fail (no unlocked dual-run). */
export const CROSS_TAB_REFRESH_MAX_WAIT_MS = 20_000;

/** Abort hung Web Lock waits → soft-fail (no storage fallthrough / dual-rotate). */
export const WEB_LOCK_TIMEOUT_MS = 18_000;

const DEFAULT_LEASE_MS = 15_000;
const SPIN_MS = 40;

/** Soft-fail code: could not acquire cross-tab lock in time (no refresh HTTP). */
export const REFRESH_LOCK_TIMEOUT_CODE = 'REFRESH_LOCK_TIMEOUT' as const;

/**
 * Thrown when the exclusive refresh lock cannot be acquired within the wait budget.
 * Coordinators must map this to a soft failure — never run rotate unlocked.
 */
export class RefreshLockTimeoutError extends Error {
  readonly code = REFRESH_LOCK_TIMEOUT_CODE;

  constructor(message = 'Cross-tab refresh lock wait timed out') {
    super(message);
    this.name = 'RefreshLockTimeoutError';
  }
}

function createOwnerId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readLease(
  storage: Pick<Storage, 'getItem'>
): { ownerId: string; until: number } | null {
  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null || raw.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { ownerId?: unknown; until?: unknown };
    if (typeof parsed.ownerId !== 'string' || typeof parsed.until !== 'number') {
      return null;
    }
    return { ownerId: parsed.ownerId, until: parsed.until };
  } catch {
    return null;
  }
}

/**
 * Compare-and-set style acquire: write unique owner, re-read to confirm.
 * Any mid-flight collision (other owner present) fails — caller retries or times out.
 */
function tryAcquireStorageLock(
  ownerId: string,
  leaseMs: number,
  storage: Pick<Storage, 'getItem' | 'setItem'>
): boolean {
  const now = Date.now();
  const existing = readLease(storage);
  if (existing !== null && existing.until > now && existing.ownerId !== ownerId) {
    return false;
  }
  const until = now + leaseMs;
  storage.setItem(STORAGE_KEY, JSON.stringify({ ownerId, until }));

  // CAS confirm — another tab may have overwritten between setItem and here.
  const confirmed = readLease(storage);
  if (confirmed === null) {
    return false;
  }
  if (confirmed.ownerId === ownerId && confirmed.until === until) {
    return true;
  }
  // Collision: other writer won (or lease mutated). If they still hold a valid
  // lease we must not proceed; if corrupt/expired, still fail this attempt.
  return false;
}

/** True iff `ownerId` still holds a non-expired lease (detect mid-flight steal). */
function stillHoldsStorageLock(
  ownerId: string,
  storage: Pick<Storage, 'getItem'>
): boolean {
  const lease = readLease(storage);
  return lease !== null && lease.ownerId === ownerId && lease.until > Date.now();
}

function releaseStorageLock(
  ownerId: string,
  storage: Pick<Storage, 'getItem' | 'removeItem'>
): void {
  const existing = readLease(storage);
  if (existing === null || existing.ownerId === ownerId) {
    storage.removeItem(STORAGE_KEY);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export type WithCrossTabRefreshLockOptions = {
  /** Override storage-lock wait budget (tests). */
  maxWaitMs?: number;
  /** Override Web Lock AbortSignal budget (tests). */
  webLockTimeoutMs?: number;
  leaseMs?: number;
};

async function withStorageRefreshLock<T>(
  fn: () => Promise<T>,
  leaseMs: number,
  maxWaitMs: number
): Promise<T> {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return fn();
  }
  const storage = window.localStorage;
  const ownerId = createOwnerId();
  const started = Date.now();

  while (Date.now() - started < maxWaitMs) {
    if (!tryAcquireStorageLock(ownerId, leaseMs, storage)) {
      await sleep(SPIN_MS);
      continue;
    }
    // Harden against concurrent writers stealing between CAS confirm and run.
    if (!stillHoldsStorageLock(ownerId, storage)) {
      await sleep(SPIN_MS);
      continue;
    }
    try {
      return await fn();
    } finally {
      releaseStorageLock(ownerId, storage);
    }
  }

  throw new RefreshLockTimeoutError();
}

function isLockAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

async function withWebLockOrThrow<T>(
  fn: () => Promise<T>,
  webLockTimeoutMs: number
): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => {
    controller.abort();
  }, webLockTimeoutMs);
  try {
    return await navigator.locks.request(
      LOCK_NAME,
      { mode: 'exclusive', signal: controller.signal },
      () => fn()
    );
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Run `fn` while holding the exclusive cross-tab refresh lock.
 * Every customer refresh rotate must go through this (via coordinator).
 * @throws {RefreshLockTimeoutError} when the lock cannot be acquired in time
 *   (never runs `fn` unlocked after timeout — soft only; BAN reuse leeway).
 */
export async function withCrossTabRefreshLock<T>(
  fn: () => Promise<T>,
  options?: WithCrossTabRefreshLockOptions
): Promise<T> {
  const maxWaitMs = options?.maxWaitMs ?? CROSS_TAB_REFRESH_MAX_WAIT_MS;
  const webLockTimeoutMs = options?.webLockTimeoutMs ?? WEB_LOCK_TIMEOUT_MS;
  const leaseMs = options?.leaseMs ?? DEFAULT_LEASE_MS;

  if (
    typeof navigator !== 'undefined' &&
    'locks' in navigator &&
    navigator.locks !== undefined &&
    typeof navigator.locks.request === 'function'
  ) {
    try {
      return await withWebLockOrThrow(fn, webLockTimeoutMs);
    } catch (err) {
      if (err instanceof RefreshLockTimeoutError) {
        throw err;
      }
      // X-1: Abort → soft-fail. Never fall through to storage (dual-rotate / family kill).
      if (isLockAbortError(err)) {
        throw new RefreshLockTimeoutError();
      }
      throw err;
    }
  }
  return withStorageRefreshLock(fn, leaseMs, maxWaitMs);
}
